class MultipleCollectionsSlider extends HTMLElement {
  constructor() {
    super();
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.section = document.querySelector(`.section-${this.sectionId}`);
    
    if (!this.section) return;

    this.selectors = {
      tabs: '.multiple-collections-slider__tab',
      collectionContainers: '.multiple-collections-slider__collection',
      productsWrapper: '.multiple-collections-slider__products-wrapper',
      products: '.multiple-collections-slider__products',
      viewAllLink: '[data-view-all-link]',
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev'
    };

    this.elements = {
      tabs: this.section.querySelectorAll(this.selectors.tabs),
      collectionContainers: this.querySelectorAll(this.selectors.collectionContainers),
      productsWrapper: this.querySelector(this.selectors.productsWrapper),
      products: this.querySelector(this.selectors.products),
      viewAllLink: this.section.querySelector(this.selectors.viewAllLink)
    };

    this.productsLimit = parseInt(this.dataset.productsLimit) || 8;
    this.columnsDesktop = parseInt(this.dataset.columnsDesktop) || 4;
    this.columnsTablet = parseInt(this.dataset.columnsTablet) || 3;
    this.columnsMobile = parseInt(this.dataset.columnsMobile) || 2;
    this.imageSizes = this.dataset.imageSizes || '';
    this.imageRatio = this.dataset.imageRatio || 'adapt';

    this.sliderInstance = null;
    this.activeCollectionHandle = null;
    const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
    mql.onchange = this.init.bind(this);
    this.handleResize = this.debounce(() => {
      if (this.sliderInstance) {
        const activeContainer = Array.from(this.elements.collectionContainers).find(
          container => !container.classList.contains('hidden')
        );
        if (activeContainer) {
          try {
            if (this.sliderInstance.slider && typeof this.sliderInstance.slider.destroy === 'function') {
              this.sliderInstance.slider.destroy(true, true);
            }
          } catch (e) {
          }
          this.sliderInstance = null;
          requestAnimationFrame(() => {
            this.initSwiper(activeContainer);
          });
        }
      }
    }, 250);
    
    window.addEventListener('resize', this.handleResize);
    this.init();
  }

  init() {
    const activeTab = this.section.querySelector(`${this.selectors.tabs}.active`);
    if (activeTab) {
      this.activeCollectionHandle = activeTab.dataset.collectionHandle;
    }
    const activeContainer = Array.from(this.elements.collectionContainers).find(
      container => !container.classList.contains('hidden')
    ) || this.elements.collectionContainers[0];

    if (this.sliderInstance) {
      try {
        if (this.sliderInstance.slider && typeof this.sliderInstance.slider.destroy === 'function') {
          this.sliderInstance.slider.destroy(true, true);
        }
      } catch (e) {
      }
      this.sliderInstance = null;
    }

    if (activeContainer) {
      this.initSwiper(activeContainer);
    }
    this.elements.tabs.forEach((tab) => {
      tab.addEventListener('click', this.handleTabClick.bind(this));
    });
  }

  initSwiper(containerElement) {
    const productsWrapper = containerElement ? containerElement.querySelector(this.selectors.productsWrapper) : this.elements.productsWrapper;
    const products = containerElement ? containerElement.querySelector(this.selectors.products) : this.elements.products;
    
    if (!productsWrapper || !products) {
      return;
    }

    if (this.sliderInstance && this.sliderInstance.container !== productsWrapper) {
      try {
        if (this.sliderInstance.slider && typeof this.sliderInstance.slider.destroy === 'function') {
          this.sliderInstance.slider.destroy(true, true);
        }
      } catch (e) {
      }
      this.sliderInstance = null;
    }

    if (this.sliderInstance && this.sliderInstance.container === productsWrapper) {
      return;
    }

    const parentContainer = containerElement || productsWrapper.closest('.multiple-collections-slider__collection');
    if (parentContainer && parentContainer.classList.contains('hidden')) {
      return;
    }

    const columnGap = window.getComputedStyle(products).getPropertyValue('--column-gap') || '0px';
    let spaceBetween = 24;

    const isMobile = window.innerWidth <= 767;
    const slidesPerView = isMobile ? this.columnsMobile : (window.innerWidth <= 1023 ? this.columnsTablet : this.columnsDesktop);

    const swiperControls = this.section.querySelector('.multiple-collections-slider__slider-controls');
    const nextButton = swiperControls ? swiperControls.querySelector(this.selectors.nextEl) : null;
    const prevButton = swiperControls ? swiperControls.querySelector(this.selectors.prevEl) : null;

    this.sliderOptions = {
      slidesPerView: isMobile ? 'auto' : slidesPerView,
      spaceBetween: spaceBetween,
      navigation: {
        nextEl: nextButton,
        prevEl: prevButton,
      },
      pagination: false,
      breakpoints: {
        768: {
          slidesPerView: this.columnsTablet,
        },
        1280: {
          slidesPerView: this.columnsDesktop,
        },
      },
      loop: false,
      threshold: 2,
    };

    try {
      if (typeof window.FoxTheme !== 'undefined' && window.FoxTheme.Carousel) {
        this.sliderInstance = new window.FoxTheme.Carousel(productsWrapper, this.sliderOptions);
        this.sliderInstance.container = productsWrapper;
        this.sliderInstance.init();
        if (this.sliderInstance.slider && typeof this.sliderInstance.slider.update === 'function') {
          this.sliderInstance.slider.update();
        }
      } else if (typeof Swiper !== 'undefined') {
        this.sliderInstance = {
          container: productsWrapper,
          slider: new Swiper(productsWrapper, this.sliderOptions)
        };
        if (this.sliderInstance.slider && typeof this.sliderInstance.slider.update === 'function') {
          this.sliderInstance.slider.update();
        }
      }
    } catch (e) {
    }
  }

  handleTabClick(event) {
    const tab = event.currentTarget;
    const collectionHandle = tab.dataset.collectionHandle;
    const collectionId = tab.dataset.collectionId;

    if (collectionHandle === this.activeCollectionHandle) {
      return;
    }

    const targetContainer = Array.from(this.elements.collectionContainers).find(
      container => container.dataset.collectionHandle === collectionHandle
    );

    if (!targetContainer) {
      return;
    }

    this.elements.tabs.forEach((t) => t.classList.remove('active'));
    tab.classList.add('active');

    if (this.elements.viewAllLink) {
      this.elements.viewAllLink.href = `/collections/${collectionHandle}`;
    }
    
    this.activeCollectionHandle = collectionHandle;

    const currentContainer = Array.from(this.elements.collectionContainers).find(
      container => !container.classList.contains('hidden')
    );

    if (currentContainer && currentContainer !== targetContainer) {
      if (this.sliderInstance) {
        try {
          if (this.sliderInstance.slider && typeof this.sliderInstance.slider.destroy === 'function') {
            this.sliderInstance.slider.destroy(true, true);
          }
        } catch (e) {
        }
        this.sliderInstance = null;
      }

      targetContainer.classList.remove('hidden');

      requestAnimationFrame(() => {
        currentContainer.classList.add('hidden');
      });

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.initSwiper(targetContainer);
        });
      });
    } else {
      targetContainer.classList.remove('hidden');
      
      this.elements.collectionContainers.forEach(container => {
        if (container !== targetContainer) {
          container.classList.add('hidden');
        }
      });
      
      if (this.sliderInstance) {
        try {
          if (this.sliderInstance.slider && typeof this.sliderInstance.slider.destroy === 'function') {
            this.sliderInstance.slider.destroy(true, true);
          }
        } catch (e) {
        }
        this.sliderInstance = null;
      }
      
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.initSwiper(targetContainer);
        });
      });
    }

    this.dispatchEvent(new CustomEvent('collection:loaded', {
      detail: { collectionHandle, collectionId }
    }));
  }

  disconnectedCallback() {
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }
    if (this.sliderInstance) {
      try {
        if (this.sliderInstance.slider && typeof this.sliderInstance.slider.destroy === 'function') {
          this.sliderInstance.slider.destroy(true, true);
        }
      } catch (e) {
      }
    }
  }

}

if (!customElements.get('multiple-collections-slider')) {
  customElements.define('multiple-collections-slider', MultipleCollectionsSlider);
}
